import { AnnotationMarkStates, AnnotationTypes } from '@atlaskit/adf-schema';

import {
	type CommentData,
	type CommentsDataMap,
	type DataMap,
	type ReplyData,
	CommentType,
} from '@confluence/comments-data';
import { fg } from '@confluence/feature-gating';
import type {
	ResolvedCommentType,
	ResolvedCommentsQueryType,
	ResolvedInlineCommentLocationType,
} from '@confluence/comments-panel-queries';
import type { CommentsReadMap } from '@confluence/comments-panel-utils';

const setCommentProperties = (
	properties: Record<string, any>,
	comments: Record<string, CommentData>,
) =>
	Object.fromEntries(
		Object.entries(comments).map(([id, comment]) => [id, { ...comment, ...properties }]),
	);

export const getResolvedAnnotationState = (unresolvedInlineComments: string[]) =>
	Object.fromEntries(
		unresolvedInlineComments.map((annotationId) => [
			annotationId,
			{
				id: annotationId,
				annotationType: AnnotationTypes.INLINE_COMMENT,
				state: AnnotationMarkStates.RESOLVED,
			},
		]),
	);

const createCommentsDataMap = (
	commentsDataMap: CommentsDataMap,
	propertyUpdate: Record<string, any>,
) =>
	Object.entries(commentsDataMap).reduce(
		(updatedCommentsDataMap, [type, comments]) => ({
			...updatedCommentsDataMap,
			[type]: setCommentProperties(propertyUpdate, comments),
		}),
		{} as CommentsDataMap,
	);

const transformCommentsToIdSets = (dataMap: DataMap): Record<string, Set<string>> => {
	return Object.fromEntries(
		Object.entries(dataMap).map(([commentId, comment]) => [
			commentId,
			new Set([comment.id, ...(comment.replies ? comment.replies.map(({ id }) => id) : [])]),
		]),
	);
};

export const getCommentIdsByTypeAndAnnotationId = (
	commentsDataMap: CommentsDataMap,
): CommentsReadMap => ({
	inline: transformCommentsToIdSets(commentsDataMap.inline),
	general: transformCommentsToIdSets(commentsDataMap.general),
});

export const createReadCommentsDataMap = (commentsDataMap: CommentsDataMap) =>
	createCommentsDataMap(commentsDataMap, { isUnread: false });

export const combineAllSets = (obj: CommentsReadMap): Set<string> =>
	Object.values(obj).reduce(
		(combinedSet, nestedObj) =>
			new Set([...combinedSet, ...Object.values(nestedObj).flatMap((set) => [...set])]),
		new Set<string>(),
	);

const extractAllComments = (commentsDataMap: CommentsDataMap): CommentData[] =>
	Object.values(commentsDataMap).flatMap((dataMap) => Object.values(dataMap));

const excludeDraftComments = (comments: CommentData[]): CommentData[] =>
	comments.filter((comment) => comment.contentStatus !== 'draft');

const extractUnreadCommentIds = (comments: (CommentData | ReplyData)[]): string[] =>
	comments.filter((comment) => comment.isUnread).map((comment) => comment.id);

export const computeUnreadCommentIds = (
	commentsDataMap: CommentsDataMap,
	commentsMarkedAsRead: CommentsReadMap,
	includeDrafts: boolean,
): Set<string> => {
	const allParentComments = includeDrafts
		? extractAllComments(commentsDataMap)
		: excludeDraftComments(extractAllComments(commentsDataMap));

	const allCommentReplies = allParentComments.flatMap(({ replies }) => replies);
	const allComments = [...allParentComments, ...allCommentReplies];
	const unreadIds = extractUnreadCommentIds(allComments);

	const readIds = combineAllSets(commentsMarkedAsRead);

	return new Set(unreadIds.filter((id) => !readIds.has(id)));
};

const extractUnresolvedCommentIds = (comments: CommentData[]): string[] =>
	comments.filter((comment) => comment.isOpen).map((comment) => comment.id);

export const computeResolvedCommentIds = (
	commentsDataMap: CommentsDataMap,
	includeDrafts: boolean,
) => {
	const allParentComments = includeDrafts
		? extractAllComments(commentsDataMap)
		: excludeDraftComments(extractAllComments(commentsDataMap));

	return extractUnresolvedCommentIds(allParentComments);
};

const COMMENTS_LOCATIONS = {
	INLINE: 'INLINE',
	FOOTER: 'FOOTER',
};

export const updateResolvedComments = (
	resolvedComments: ResolvedCommentsQueryType,
	commentData: CommentsDataMap,
): CommentsDataMap => {
	if (!resolvedComments?.comments?.nodes) return commentData;
	const resolvedCommentNodes: ResolvedCommentType[] = resolvedComments.comments.nodes.filter(
		(node): node is ResolvedCommentType => node !== null,
	);

	const updateComments = (
		comments: Record<string, any>,
		locationType: string,
		commentType: string,
	) =>
		Object.keys(comments).reduce((acc, key) => {
			const comment = comments[key];
			const resolvedComment = resolvedCommentNodes.find((resolved) =>
				locationType === COMMENTS_LOCATIONS.INLINE
					? (resolved.location as ResolvedInlineCommentLocationType).inlineMarkerRef === key &&
						resolved.location.type === locationType
					: resolved.id === key && resolved.location.type === locationType,
			);

			if (resolvedComment && (comment.isOpen || comment.isUnread)) {
				return {
					...acc,
					[key]: { ...resolvedComment, isOpen: false, isUnread: false, type: commentType },
				};
			} else {
				return {
					...acc,
					[key]: comment,
				};
			}
		}, {});

	const updatedInlineComments = updateComments(
		commentData.inline,
		COMMENTS_LOCATIONS.INLINE,
		CommentType.INLINE,
	);
	const updatedGeneralComments = updateComments(
		commentData.general,
		COMMENTS_LOCATIONS.FOOTER,
		CommentType.GENERAL,
	);

	return {
		inline: updatedInlineComments,
		general: updatedGeneralComments,
	};
};

type DeleteParentCommentParams = {
	isReply: boolean;
	threadKey: string;
	commentType: CommentType;
	commentsDataMap: CommentsDataMap;
	deleteParentComment: (params: { threadKey: string; commentType: CommentType }) => void;
};

export const deleteInlineParentComment = ({
	isReply,
	threadKey,
	commentType,
	commentsDataMap,
	deleteParentComment,
}: DeleteParentCommentParams): CommentsDataMap | null => {
	if (isReply || !fg('confluence_frontend_dangling_comments')) {
		return null;
	}

	const deletedParentComment = {
		[CommentType.INLINE]: { [threadKey]: commentsDataMap.inline[threadKey] },
		[CommentType.GENERAL]: {},
	};

	deleteParentComment({
		threadKey,
		commentType,
	});

	return deletedParentComment;
};
